import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, FileText, Shield, Database, Trash2, Cookie, ExternalLink, Mail, Phone, MapPin, Clock, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';

const LegalPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const legalDocuments = [
    {
      title: 'Privacy Policy',
      description: 'Comprehensive overview of how we collect, use, and protect your personal information across all our services.',
      icon: Shield,
      path: '/privacy',
      color: 'blue',
      updated: 'October 24, 2024',
      category: 'privacy',
      importance: 'high',
      readTime: '8 min read'
    },
    {
      title: 'Terms of Service',
      description: 'Legal agreement outlining the terms and conditions for using Quixocial platform and services.',
      icon: FileText,
      path: '/terms',
      color: 'purple',
      updated: 'October 24, 2024',
      category: 'legal',
      importance: 'high',
      readTime: '12 min read'
    },
    {
      title: 'Data Usage Policy',
      description: 'Specific policy for Meta platform data usage, API compliance, and third-party integrations.',
      icon: Database,
      path: '/data-usage',
      color: 'green',
      updated: 'October 24, 2024',
      category: 'privacy',
      importance: 'medium',
      readTime: '6 min read'
    },
    {
      title: 'Data Deletion Guide',
      description: 'Complete step-by-step instructions for deleting your account and all associated data permanently.',
      icon: Trash2,
      path: '/data-deletion',
      color: 'red',
      updated: 'October 24, 2024',
      category: 'privacy',
      importance: 'medium',
      readTime: '4 min read'
    },
    {
      title: 'Cookie Policy',
      description: 'Detailed information about cookies, tracking technologies, and how to manage your preferences.',
      icon: Cookie,
      path: '/cookies',
      color: 'orange',
      updated: 'October 24, 2024',
      category: 'privacy',
      importance: 'low',
      readTime: '5 min read'
    },
    {
      title: 'GDPR Compliance',
      description: 'Your rights under GDPR and how to exercise them. Manage data requests and privacy preferences.',
      icon: Shield,
      path: '/gdpr-request',
      color: 'indigo',
      updated: 'October 24, 2024',
      category: 'privacy',
      importance: 'high',
      readTime: 'Interactive'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Documents', count: legalDocuments.length },
    { id: 'privacy', label: 'Privacy & Data', count: legalDocuments.filter(doc => doc.category === 'privacy').length },
    { id: 'legal', label: 'Legal Terms', count: legalDocuments.filter(doc => doc.category === 'legal').length }
  ];

  const filteredDocuments = activeCategory === 'all' 
    ? legalDocuments 
    : legalDocuments.filter(doc => doc.category === activeCategory);

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
      green: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
      red: 'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
      orange: 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
      indigo: 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const getImportanceIcon = (importance: string) => {
    switch (importance) {
      case 'high': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'medium': return <Info className="w-4 h-4 text-yellow-500" />;
      case 'low': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Navbar />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-blue-500/25 mx-auto">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-pulse">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-black text-slate-900 dark:text-white mb-6">
            Legal <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Documents</span>
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-4xl mx-auto leading-relaxed">
            Complete transparency in our legal framework. Access all policies, understand your rights, and see how we protect your data with industry-leading compliance standards.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div 
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex space-x-2 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl p-2 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-lg">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg shadow-blue-500/25 scale-105'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/50 dark:hover:bg-slate-700/50'
                }`}
              >
                {category.label}
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Documents Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {filteredDocuments.map((doc, index) => {
            const IconComponent = doc.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <Link to={doc.path} className="group block">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/80 to-white/40 dark:from-slate-800/80 dark:to-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 h-full">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
                    
                    {/* Importance Badge */}
                    <div className="absolute top-4 right-4 flex items-center space-x-1">
                      {getImportanceIcon(doc.importance)}
                    </div>
                    
                    <div className="relative p-8">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${getColorClasses(doc.color)} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {doc.title}
                      </h3>
                      
                      <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                        {doc.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="space-y-1">
                          <div className="text-slate-500 dark:text-slate-400">
                            Updated: {doc.updated}
                          </div>
                          <div className="text-slate-500 dark:text-slate-400">
                            {doc.readTime}
                          </div>
                        </div>
                        
                        <div className="flex items-center text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                          <span className="font-semibold mr-2">Read</span>
                          <ExternalLink className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Help & Support Section */}
        <motion.div 
          className="mt-20 relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/80 to-white/40 dark:from-slate-800/80 dark:to-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-blue-500/5 to-purple-500/5"></div>
          <div className="relative p-12">
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Need Legal Assistance?</h2>
              <p className="text-slate-600 dark:text-slate-300 text-lg max-w-2xl mx-auto">
                Our legal and privacy teams are here to help with any questions about our policies, your rights, or compliance matters.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: FileText,
                  title: 'General Legal',
                  description: 'Questions about policies, terms, or legal compliance',
                  contact: 'info@quixocial.com',
                  color: 'blue',
                  response: '24-48 hours'
                },
                {
                  icon: Shield,
                  title: 'Privacy & Data',
                  description: 'Data protection, privacy rights, and GDPR requests',
                  contact: 'info@quixocial.com',
                  color: 'green',
                  response: '12-24 hours'
                }
              ].map((item, index) => (
                <div key={index} className="group text-center p-6 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 hover:shadow-lg transition-all duration-300 hover:scale-105">
                  <div className={`w-14 h-14 bg-gradient-to-br ${
                    item.color === 'blue' ? 'from-blue-500 to-blue-600' :
                    item.color === 'green' ? 'from-green-500 to-green-600' :
                    'from-red-500 to-red-600'
                  } rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-2 text-lg">{item.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm mb-4 leading-relaxed">{item.description}</p>
                  <div className="space-y-2">
                    <a 
                      href={item.contact.includes('@') ? `mailto:${item.contact}` : `tel:${item.contact}`}
                      className={`block font-semibold transition-colors ${
                        item.color === 'blue' ? 'text-blue-600 hover:text-blue-700' :
                        item.color === 'green' ? 'text-green-600 hover:text-green-700' :
                        'text-red-600 hover:text-red-700'
                      }`}
                    >
                      {item.contact}
                    </a>
                    <div className="flex items-center justify-center text-xs text-slate-500 dark:text-slate-400">
                      <Clock className="w-3 h-3 mr-1" />
                      Response: {item.response}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Compliance & Certifications */}
        <motion.div 
          className="mt-12 relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-900 shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-indigo-500/10"></div>
          <div className="relative p-12 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">Compliance & Certifications</h3>
            <p className="text-slate-300 mb-8 text-lg max-w-3xl mx-auto">
              Quixocial maintains the highest standards of data protection, privacy compliance, and security certifications to protect your business and data.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'GDPR Ready', status: 'active' },
                { label: 'CCPA Ready', status: 'active' },
                { label: 'Meta Certification', status: 'planned' },
                { label: 'ISO Certification', status: 'planned' }
              ].map((cert, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center justify-center mb-2">
                    {cert.status === 'active' ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : cert.status === 'planned' ? (
                      <Clock className="w-5 h-5 text-blue-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-400" />
                    )}
                  </div>
                  <div className="text-white font-semibold text-sm">{cert.label}</div>
                  <div className="text-slate-300 text-xs mt-1">
                    {cert.status === 'active' ? 'Implemented' : cert.status === 'planned' ? 'Planned' : 'In Progress'}
                  </div>
                </div>
              ))}
            </div>

            {/* Compliance Disclaimer */}
            <div className="bg-blue-900/20 border border-blue-400/30 rounded-xl p-4 mb-8">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-white text-xs">ℹ</span>
                </div>
                <div className="text-sm text-blue-100">
                  <p className="font-semibold mb-2">Privacy & Security Commitment</p>
                  <p className="text-blue-200 leading-relaxed">
                    "GDPR Ready" and "CCPA Ready" indicate our platform implements comprehensive privacy controls, 
                    data protection measures, and user rights management. Formal certifications are planned as we scale. 
                    We prioritize your data security and privacy compliance from day one.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-slate-400">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Last updated: October 2025 • Next audit: March 2026</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LegalPage;